import { DEFAULT_SECTION } from './defaultSection'
import { BaseFieldClass } from '../fields/baseField.class'

import { FieldClass } from '@/api/fields'
import { formatDateApi } from '@/api/formatter'
import { Layout } from '@/models/layout/layout.class'
import { IFieldRequestRecord, IFilterValueType, ISectionsProps, ISectionParam, ISectionWidget, IServerSection } from '@/types/sections.type'
import { IGridAreas } from '@/types/serverGrid.type'

export class SectionClass {
  id: number
  layout: Layout
  formLayout: Layout
  grid: IGridAreas | null
  viewGrid: IGridAreas | null
  sectionsGrid: IGridAreas | null
  sectionsViewGrid: IGridAreas | null
  gridArea: string
  name: string
  style: string
  visible: boolean
  weight: number | ''
  widgets: ISectionWidget[] = []
  Params: ISectionParam[] = []
  fields: BaseFieldClass[] = []
  children: SectionClass[] = []
  type: '' | 'expansion'
  expanded: boolean

  constructor (data: IServerSection) {
    this.id = data.Index
    this.name = data.Name || ''
    this.weight = data.Weight || ''
    this.widgets = data.Widgets || []
    this.Params = (data.Params || []).slice().sort((a, b) => a.weight - b.weight)
    this.layout = new Layout(data.Layout)
    this.formLayout = new Layout(data.FormLayout)
    this.grid = data.Grid || null
    this.viewGrid = data.VGrid || null
    this.sectionsGrid = data.SectionsGrid || null
    this.sectionsViewGrid = data.SectionsVGrid || null
    this.gridArea = data.GridArea || ''
    this.style = data.Style || ''
    this.visible = data.Visible

    this.children = (data?.Children || []).map(s => new SectionClass(s))
    this.type = data?.Type as '' | 'expansion'
    this.expanded = data?.Expanded as boolean

    // Добавим поля учитывая вес (weight)
    Object.values(data?.Fields || {})
      .reverse()
      .forEach(field => {
        this.addField(new FieldClass(field))
      })
  }

  private addField (field: BaseFieldClass): void {
    const position = this.getPosition(field)
    this.fields.splice(position, 0, field)
  }

  private getPosition (field: BaseFieldClass): number {
    // Использование бинарного поиска для нахождения правильной позиции для вставки
    let left = 0
    let right = this.fields?.length

    while (left < right) {
      const mid = Math.floor((left + right) / 2)
      const currentField = this.fields[mid]
      if (currentField && currentField.weight < field.weight) {
        left = mid + 1
      } else {
        right = mid
      }
    }

    return left
  }

  getFieldByName (name: string) {
    return this.fields
      .find(field => field.name === name)
  }

  setUid (uid: number): void {
    this.fields.forEach(field => {
      field._uid = uid
    })
    this.children.forEach(child => child.setUid(uid))
  }
}

export class SectionsClass {
  private _sections: Map<number, IServerSection> = new Map()

  sections: SectionClass[] = []
  sectionsGrid: IGridAreas | null
  sectionsViewGrid: IGridAreas | null
  params: ISectionsProps['params']
  isDeleted: boolean = false
  // recordUuid добавлен для работы редактирования смен
  // https://betr.uchi.red/#!/directory/schedules/item/c91ae38a-eeb9-434f-904e-c6dc9b24692a/tab/shifts
  recordUuid: string | undefined

  constructor (data: ISectionsProps) {
    this.sectionsGrid = data.sectionsParams?.Grid || null
    this.sectionsViewGrid = data.sectionsParams?.VGrid || null
    if (!data.sections?.length) {
      data.sections = [DEFAULT_SECTION]
    }
    this.prepareSections(data)

    // собираем дерево
    const tree = this.buildTree()

    // только корневые секции
    this.sections = tree.map(section => new SectionClass(section))

    this.isDeleted = data?.params?.Deleted || false
    this.params = data?.params
    this.recordUuid = data?.recordUuid
  }

  private prepareSections (data: ISectionsProps) {
    const sections = data.sections || []
    const fields = Object.values(data.fields || {})

    // инициализация секций
    sections.forEach(section => {
      section.Fields = []
      section.Children = []
      this._sections.set(section.Index, section)
    })

    // раскидываем поля по секциям
    fields.forEach((field: any) => {
      const sectionId = field.GridPlacement?.section ?? field.Layout?.Section
      if (sectionId !== undefined) {
        const section = this._sections.get(sectionId)
        if (section && Array.isArray(section.Fields)) {
          section.Fields.push(field)
        }
      }
    })
  }

  private buildTree () {
    const roots: any[] = []

    this._sections.forEach(section => {
      const parentIndex = section.ParentIndex ?? null

      if (parentIndex === -1 || parentIndex === null || parentIndex === section.Index) {
        // корневая секция
        roots.push(section)
      } else {
        const parent = this._sections.get(parentIndex)
        parent?.Children?.push(section)
      }
    })

    return roots
  }

  // Обход всего дерева сохраняет общий контекст полей независимо от глубины.
  getAllSections (): SectionClass[] {
    const collect = (sections: SectionClass[]): SectionClass[] =>
      sections.flatMap(section => [section, ...collect(section.children ?? [])])
    return collect(this.sections)
  }

  getAllFields (): BaseFieldClass[] {
    return this.getAllSections().flatMap(section => section.fields)
  }

  getSectionById (id: number): SectionClass | undefined {
    return this.getAllSections().find(section => section.id === id)
  }

  getValues (): FormData {
    const formData = new FormData()

    this.getAllSections().forEach(section => {
      section.fields.forEach(field => {
        try {
          if (field.name === 'WorkplaceUuid' && field.fieldValue === '') return
          if (field.name === 'Files' && field.fieldValue === null) return
          if (field.name === 'NewVersion') {
            formData.append(field.name, field.fieldValue === true ? 'true' : '')
            return
          }

          // Не отправлять на сервер данные полей с readonly true (gorn-710)
          if (field.readonly) return

          if (field.type === 'data' && field.widgetType === 'string') return
          if (field.type === 'fileList' && !field.value) {
            return
          }

          field.appendToFormData(formData)
        } catch (error) {
          console.error('Ошибка в getValues: ' + error)
        }
      })
    })

    return formData
  }

  /**
   * Собирает значения для JSON API бандлов.
   */
  getRequestValues (): IFieldRequestRecord[] {
    return this.getAllSections().flatMap(section =>
      section.fields
        .filter(field => !field.readonly)
        .map(field => ({
          Name: field.name,
          Value: field.valueToServer()
        }))
    )
  }

  getFilterValues (): IFilterValueType[] {
    return this.getAllSections().flatMap(section =>
      section.fields.map(field => ({
        Name: field.name,
        Operator: field.filterOperator,
        Value: field.getFilterValue()
      }))
    )
  }

  setFilterValues (filter: Record<string, unknown>): void {
    for (const section of this.getAllSections()) {
      for (const field of section.fields) {
        const value = filter[field.name]
        if (value === undefined) continue
        field.setFilterValue(value)
      }
    }
  }

  resetFilterValues (): void {
    for (const section of this.getAllSections()) {
      for (const field of section.fields) {
        field.fieldValue = field.initialValue
      }
    }
  }

  getFilterEmployeeEducationValues (): Array<{ Name: string; Value?: unknown }> {
    return this.getAllSections().flatMap(section =>
      section.fields.flatMap(field => {
        if (field.name === 'Author' || field.name === 'ParentUuid') return []
        if (field.name === 'EducationLevel') return [{ Name: field.name, Value: (field.value as any)?.value }]
        if (field.name === 'Empty') return [{ Name: field.name }]
        if (field.name === 'IssueDate') return [{ Name: field.name, Value: formatDateApi(field.value as Date) }]
        return [{ Name: field.name, Value: field.value ?? '' }]
      })
    )
  }

  isValid (): boolean {
    return this.getAllSections().every(section =>
      section.fields.every(field => {
        if (field.type === 'hidden' || !field.visible) return true

        const hasError = !!field.errorMessage?.length

        if (hasError) {
          return false
        }

        return !field.required || field.hasValue()
      })
    )
  }

  checkValues (): boolean {
    this.getAllSections().forEach(section => {
      section.fields.forEach(field => field.checkValue())
    })

    return this.isValid()
  }

  getFieldByName (name: string) {
    return this.getAllSections()
      .flatMap(section => section.fields)
      .find(field => field.name === name)
  }

  setUid (uid: number): void {
    this.sections.forEach(section => section.setUid(uid))
  }
}
